import type { SceneListItem, SceneMapEntry } from "@chuzi/shared/types";
import { api } from "./api";

export interface StoryMediaBundle {
  /** media_id → resolved URL */
  urlByMediaId: Map<string, string>;
  /** media_id or url → decoded audio buffer */
  audioBuffers: Map<string, AudioBuffer>;
  /** media_id or url → loaded image */
  images: Map<string, HTMLImageElement>;
  /** media_id or url → buffered video element */
  videos: Map<string, HTMLVideoElement>;
}

function collectMediaIds(
  scenes: SceneListItem[],
  sceneMap: Record<string, SceneMapEntry>,
): Set<string> {
  const ids = new Set<string>();
  for (const scene of scenes) {
    if (scene.media_id) ids.add(scene.media_id);
    if (scene.alt_media_id) ids.add(scene.alt_media_id);
    const lines = scene.text_content?.lines ?? [];
    for (const line of lines) {
      if (line.media_id) ids.add(line.media_id);
    }
    const entry = sceneMap[scene.id];
    if (!entry) continue;
    for (const choice of entry.choices) {
      if (choice.choice_icon_media_id) ids.add(choice.choice_icon_media_id);
    }
  }
  return ids;
}

function preloadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`image preload failed: ${url}`));
    img.src = url;
  });
}

function preloadVideo(url: string): Promise<HTMLVideoElement> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    const onReady = () => {
      cleanup();
      resolve(video);
    };
    const onError = () => {
      cleanup();
      reject(new Error(`video preload failed: ${url}`));
    };
    const cleanup = () => {
      video.removeEventListener("canplaythrough", onReady);
      video.removeEventListener("error", onError);
    };
    video.addEventListener("canplaythrough", onReady);
    video.addEventListener("error", onError);
    video.src = url;
    video.load();
  });
}

async function decodeAudio(url: string, ctx: AudioContext): Promise<AudioBuffer> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`audio fetch failed: ${url}`);
  const data = await res.arrayBuffer();
  return ctx.decodeAudioData(data);
}

function isLikelyVideo(url: string, mediaType?: string | null): boolean {
  if (mediaType?.startsWith("video/")) return true;
  const path = url.split("?")[0].toLowerCase();
  return /\.(mp4|webm|mov|m3u8)(\?|$)/.test(path) || path.includes(".m3u8");
}

function isLikelyImage(url: string, mediaType?: string | null): boolean {
  if (mediaType?.startsWith("image/")) return true;
  const path = url.split("?")[0].toLowerCase();
  return /\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/.test(path);
}

function isLikelyAudio(url: string, mediaType?: string | null): boolean {
  if (mediaType?.startsWith("audio/")) return true;
  const path = url.split("?")[0].toLowerCase();
  return /\.(mp3|wav|ogg|aac|m4a|flac)(\?|$)/.test(path);
}

export async function buildStoryMediaBundle(
  scenes: SceneListItem[],
  sceneMap: Record<string, SceneMapEntry>,
  onProgress?: (ratio: number) => void,
): Promise<StoryMediaBundle> {
  const urlByMediaId = new Map<string, string>();
  const mediaIds = collectMediaIds(scenes, sceneMap);

  for (const scene of scenes) {
    const entry = sceneMap[scene.id];
    if (entry?.media_url && scene.media_id) {
      urlByMediaId.set(scene.media_id, entry.media_url);
    }
    for (const choice of entry?.choices ?? []) {
      if (choice.choice_icon_media_id && choice.choice_icon_url) {
        urlByMediaId.set(choice.choice_icon_media_id, choice.choice_icon_url);
      }
    }
  }

  const unresolved = [...mediaIds].filter((id) => !urlByMediaId.has(id));
  await Promise.all(
    unresolved.map(async (id) => {
      try {
        const res = await api.media.sourceUrl(id);
        urlByMediaId.set(id, res.url);
      } catch {
        /* skip unresolvable media */
      }
    }),
  );

  const tasks: Array<{ key: string; url: string; kind: "audio" | "image" | "video"; mediaType?: string | null }> = [];

  for (const [mediaId, url] of urlByMediaId) {
    const sceneEntry = Object.values(sceneMap).find((e) => e.media_url === url);
    const mediaType = sceneEntry?.media_type ?? null;
    if (isLikelyVideo(url, mediaType)) {
      tasks.push({ key: mediaId, url, kind: "video", mediaType });
    } else if (isLikelyImage(url, mediaType)) {
      tasks.push({ key: mediaId, url, kind: "image", mediaType });
    } else if (isLikelyAudio(url, mediaType)) {
      tasks.push({ key: mediaId, url, kind: "audio", mediaType });
    } else {
      tasks.push({ key: mediaId, url, kind: "audio", mediaType });
    }
  }

  for (const entry of Object.values(sceneMap)) {
    if (entry.media_url && !tasks.some((t) => t.url === entry.media_url)) {
      const kind = isLikelyVideo(entry.media_url, entry.media_type)
        ? "video"
        : isLikelyImage(entry.media_url, entry.media_type)
          ? "image"
          : "video";
      tasks.push({
        key: entry.scene_id,
        url: entry.media_url,
        kind,
        mediaType: entry.media_type,
      });
    }
    for (const choice of entry.choices) {
      if (choice.choice_icon_url && !tasks.some((t) => t.url === choice.choice_icon_url)) {
        tasks.push({
          key: choice.id,
          url: choice.choice_icon_url,
          kind: "image",
        });
      }
    }
  }

  const audioCtx = new AudioContext();
  const audioBuffers = new Map<string, AudioBuffer>();
  const images = new Map<string, HTMLImageElement>();
  const videos = new Map<string, HTMLVideoElement>();

  let done = 0;
  const total = Math.max(tasks.length, 1);

  await Promise.all(
    tasks.map(async (task) => {
      try {
        if (task.kind === "audio") {
          const buf = await decodeAudio(task.url, audioCtx);
          audioBuffers.set(task.key, buf);
          audioBuffers.set(task.url, buf);
        } else if (task.kind === "image") {
          const img = await preloadImage(task.url);
          images.set(task.key, img);
          images.set(task.url, img);
        } else {
          const video = await preloadVideo(task.url);
          videos.set(task.key, video);
          videos.set(task.url, video);
        }
      } catch {
        /* non-fatal — scene may still play without this asset */
      } finally {
        done += 1;
        onProgress?.(done / total);
      }
    }),
  );

  await audioCtx.close().catch(() => {});

  return { urlByMediaId, audioBuffers, images, videos };
}
